/**
 * ══════════════════════════════════════════════════════════════════════════
 * ZAMAN → MİLİSANİYE — TEK OTORİTE (saf: import yok, DOM yok, I/O yok)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ── ÖLÇÜLEN KUSUR (2026-08-25) ───────────────────────────────────────────
 * Altı ayrı dosya, gövdesi neredeyse birebir aynı olan kendi `ts()` /
 * `toMillis()` kopyasını taşıyordu ve HEPSİ saniye/milisaniye ayracını
 * **`2e12`** yazıyordu:
 *
 *     if (typeof val === "number") return val < 2e12 ? val * 1000 : val;
 *
 * Bu eşik, saniyeyle milisaniyeyi **2033 yılına kadar AYIRT EDEMEZ**.
 * Ölçüldü (bu turun gerçek epoch-ms'i):
 *
 *     1787612926121  <  2e12   →  kod bunu SANİYE sanıyor  →  ×1000
 *     1787612926121000         →  new Date(...).getFullYear() === 58617
 *
 * Yani ham milisaniye damgası taşıyan **tek bir kayıt**, tarih DESC sıralı
 * her listenin tepesine **kalıcı olarak çivilenir** ve gösterimde 58 617
 * yılını basar. Etkilenen sıralamalar ölçüldü: `ClipsDesktop:374,938`,
 * `MyClips:591`, `UserPosts:759` (ızgara `getCreated`).
 *
 * ── DOĞRU AYRAÇ: 1e11 ────────────────────────────────────────────────────
 * İki alan arasında **hiçbir gerçek tarihin düşmediği** tek boşluk:
 *   · `1e11` SANİYE olarak okunursa  →  5138 yılı
 *   · `1e11` MİLİSANİYE olarak okunursa →  1973 yılı
 * Ürünün ürettiği hiçbir damga bu aralığa düşmez; `2e12` ise bugünün
 * milisaniyesini tam ortasından ikiye böler.
 *
 * Bu gerekçe `src/yorumBirlesim.ts`te (yorum hattı) ölçülerek yazılmıştı;
 * bu modül o düzeltmeyi **TEK GÖVDEYE** indirir — altıncı kopya yazılamaz.
 * `yorumBirlesim.zamanMs` artık buradan yeniden dışa aktarılır.
 *
 * ── SÖZLEŞME GENİŞLEDİ, DARALMADI (emsal: utils/klipAlanlari.ts) ─────────
 * Altı kopyanın tanıdığı biçimler AYRIK kümelerdi; birleşim alındı:
 *   · `toMillis()`  — `utils.ts` + `yorumBirlesim` tanıyordu, 4 kopya tanımıyordu
 *   · `_seconds`    — `ClipsDesktop`/`MyClips`/`UserPosts` tanıyordu, 3 kopya tanımıyordu
 *   · `toDate()`    — `utils.ts` + `ClipDetailModalDesktop` tanıyordu, 4 kopya tanımıyordu
 *   · `Date`        — hiçbiri AÇIKÇA tanımıyordu (dördü `Date.parse(String(d))`
 *                     ile saniye hassasiyetine yuvarlıyordu, ikisi `0` dönüyordu)
 * Genişleme yalnız **eskiden `0` (veya yuvarlanmış) dönen** dallara dokunur;
 * hiçbir kopyanın doğru çözdüğü bir girdi başka bir sonuca gitmez.
 *
 * ⚠️ TEK ÖLÇÜLEN DAVRANIŞ DELTASI — SANİYE-ALTI HASSASİYET:
 * Gerçek Firestore `Timestamp`i hem `toMillis()` hem `seconds` taşır.
 * Beş kopya `seconds * 1000` okuyordu (saniyeye yuvarlar), bu modül
 * `toMillis()`i önce okur (nanosaniye payını korur). Sonuç aynı saniye
 * içinde ≤999 ms farklı olabilir. Gösterim yüzeylerinde görünmez
 * (`formatDateTR` gün, göreli zaman saniye çözünürlüğünde); sıralamada
 * **daha doğru** — aynı saniyede yazılmış iki kayıt artık gerçek sırasında
 * gelir, eskiden berabere kalıp dizi sırasına düşüyorlardı.
 * ══════════════════════════════════════════════════════════════════════════
 */

/**
 * SANİYE ↔ MİLİSANİYE ayracı.
 *
 * Bundan KÜÇÜK sayı saniye kabul edilip 1000 ile çarpılır; büyük/eşit olan
 * zaten milisaniyedir. Sabit dışa açık: kapı testi onu ölçer, ikinci bir
 * yere kopyalanmasın diye.
 */
export const SANIYE_MS_AYRACI = 1e11;

/** Ördek-tipli zaman nesnesi — SDK örneği de düz JSON kopyası da olabilir. */
type ZamanNesnesi = {
  toMillis?: unknown;
  seconds?: unknown;
  _seconds?: unknown;
  toDate?: unknown;
};

/** Sonlu sayıysa kendisi, değilse `0`. */
function sonluYaDaSifir(deger: unknown): number {
  return typeof deger === "number" && Number.isFinite(deger) ? deger : 0;
}

/**
 * Bilinmeyen bir zaman değerini milisaniyeye çevirir; çözülemezse `0`.
 *
 * `0` bilerek "çözülemedi" demektir (1970 değil): altı çağrı yerinin hepsi
 * `if (!val) return 0` ile başlıyordu ve `0`ı "zaman yok" sayıyordu. Tarih
 * DESC sıralamada `0` listenin SONUNA düşer — bu kusurun tersi, güvenli yön.
 *
 * Tanınan biçimler: sayı (saniye|milisaniye, `SANIYE_MS_AYRACI` ile ayrılır) ·
 * ISO/`Date.parse` metni · `Date` · Firestore `Timestamp` (`toMillis()`) ·
 * düz `{ seconds }` / `{ _seconds }` · `{ toDate() }`.
 */
export function zamanMs(deger: unknown): number {
  if (deger === null || deger === undefined || deger === "") return 0;

  // ── SAYI: kusurun bulunduğu dal ────────────────────────────────────────
  if (typeof deger === "number") {
    if (!Number.isFinite(deger)) return 0;
    return deger < SANIYE_MS_AYRACI ? deger * 1000 : deger;
  }

  if (typeof deger === "boolean") return 0;

  if (deger instanceof Date) return sonluYaDaSifir(deger.getTime());

  if (typeof deger === "string") {
    const t = Date.parse(deger);
    return Number.isFinite(t) ? t : 0;
  }

  if (typeof deger !== "object") return 0;

  const nesne = deger as ZamanNesnesi;

  // Firestore `Timestamp` — en hassas okuma. `try`: SDK dışı sahte nesnede
  // fırlayabilir; fırlarsa aşağıdaki dallar denenmeye devam eder (eski
  // `yorumBirlesim` gövdesi burada `0` dönüyordu — o daralma kaldırıldı).
  if (typeof nesne.toMillis === "function") {
    try {
      const ms = (nesne.toMillis as () => unknown)();
      if (typeof ms === "number" && Number.isFinite(ms)) return ms;
    } catch {
      /* aşağıdaki dallara düş */
    }
  }

  // Düz `{seconds}` / `{_seconds}` — cache, JSON köprüsü, test fixture'ı.
  // `instanceof Timestamp` KULLANILMADI: bu şekiller SDK örneği DEĞİL.
  if (typeof nesne.seconds === "number" && Number.isFinite(nesne.seconds)) {
    return nesne.seconds * 1000;
  }
  if (typeof nesne._seconds === "number" && Number.isFinite(nesne._seconds)) {
    return nesne._seconds * 1000;
  }

  if (typeof nesne.toDate === "function") {
    try {
      const d = (nesne.toDate as () => unknown)();
      if (d instanceof Date) return sonluYaDaSifir(d.getTime());
    } catch {
      /* aşağıdaki dala düş */
    }
  }

  // Son çare — dört kopyanın taşıdığı `Date.parse(String(v))` dalı birebir
  // korundu. Tanınmayan nesnede `"[object Object]"` → NaN → 0.
  const t = Date.parse(String(deger));
  return Number.isFinite(t) ? t : 0;
}

export default zamanMs;
